import { useEffect, useState } from "react";
import { api, fmt, fmtInt, resolveImage } from "@/lib/api";
import { Package, Boxes, IndianRupee, ReceiptText, Wallet } from "lucide-react";
import { Link } from "react-router-dom";
import { StatusBadge } from "@/components/StatusBadge";

export default function Dashboard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/dashboard").then(({ data }) => setData(data)).catch(console.error);
  }, []);

  if (!data) return <div data-testid="dashboard-loading">Loading...</div>;

  const sb = data.sales_breakdown || { gst_total: 0, non_gst_cash: 0, non_gst_online: 0 };
  const nonGstTotal = (sb.non_gst_cash || 0) + (sb.non_gst_online || 0);

  return (
    <div data-testid="dashboard-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Dashboard</h1>
          <p className="page-sub">{data.company?.name}</p>
        </div>
        <Link to="/invoices/new" className="btn btn-primary" data-testid="quick-create-invoice">+ New Invoice</Link>
      </div>

      <div className="kpi-grid" style={{ marginBottom: 20 }}>
        <Kpi icon={Package} label="Total Products" value={fmtInt(data.total_products)} tid="kpi-products"/>
        <Kpi icon={Boxes} label="Total Inventory" value={fmtInt(data.total_inventory)} foot="units in stock" tid="kpi-inventory"/>
        <Kpi icon={IndianRupee} label="Total Sales" value={`₹ ${fmt(data.total_sales)}`} foot={`${data.total_invoice_count} invoice(s) lifetime`} tid="kpi-sales"/>
      </div>

      {/* Sales breakdown */}
      <div className="card-flush" style={{ marginBottom: 20 }} data-testid="sales-breakdown">
        <div style={{ padding: "16px 20px", borderBottom: "1px solid var(--border)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <h2 style={{ fontSize: 16, fontWeight: 600 }}>Total Sales Breakdown</h2>
            <p style={{ color: "var(--text-muted)", fontSize: 12, marginTop: 2 }}>Lifetime · counted from amount received on non-cancelled invoices</p>
          </div>
        </div>
        <div style={{ padding: 20, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 16 }}>
          <BreakBox color="#EFF6FF" border="#BFDBFE" icon={ReceiptText} label="GST Billing" value={`₹ ${fmt(sb.gst_total)}`} tid="bd-gst"/>
          <BreakBox color="#F0FDF4" border="#BBF7D0" icon={Wallet} label="Non-GST · Total" value={`₹ ${fmt(nonGstTotal)}`} tid="bd-nongst" sub={
            <div style={{ display: "flex", gap: 12, marginTop: 8, fontSize: 12, color: "var(--text-muted)" }}>
              <span data-testid="bd-nongst-cash"><strong style={{ color: "var(--text)" }}>Cash:</strong> ₹ {fmt(sb.non_gst_cash)}</span>
              <span data-testid="bd-nongst-online"><strong style={{ color: "var(--text)" }}>Online:</strong> ₹ {fmt(sb.non_gst_online)}</span>
            </div>
          }/>
        </div>
      </div>

      <div className="grid-2">
        <div className="card-flush" data-testid="recent-invoices-card">
          <div style={{ padding: "16px 20px", borderBottom: "1px solid var(--border)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <h2 style={{ fontSize: 16, fontWeight: 600 }}>Recent Invoices</h2>
            <Link to="/invoices" className="btn btn-ghost btn-sm">View all</Link>
          </div>
          <table className="tbl">
            <thead><tr><th>Invoice</th><th>Customer</th><th>Status</th><th className="num">Total</th></tr></thead>
            <tbody>
              {data.recent_invoices?.length === 0 && <tr><td colSpan={4} style={{ textAlign: "center", color: "var(--text-muted)", padding: 24 }}>No invoices yet</td></tr>}
              {data.recent_invoices?.map((inv) => (
                <tr key={inv.id}>
                  <td><Link to={`/invoices/${inv.id}`} style={{ color: "var(--accent)", textDecoration: "none", fontWeight: 500 }}>{inv.invoice_number}</Link><div style={{ fontSize: 11, color: "var(--text-muted)" }}>{inv.invoice_date}</div></td>
                  <td>{inv.customer_snapshot?.name}</td>
                  <td><StatusBadge status={inv.status || "DRAFT"}/></td>
                  <td className="num">₹ {fmt(inv.grand_total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="card-flush" data-testid="low-stock-card">
          <div style={{ padding: "16px 20px", borderBottom: "1px solid var(--border)" }}>
            <h2 style={{ fontSize: 16, fontWeight: 600 }}>Low Stock Products</h2>
            <p style={{ color: "var(--text-muted)", fontSize: 12, marginTop: 2 }}>Products with one or more variants at qty ≤ 2</p>
          </div>
          <div style={{ maxHeight: 360, overflowY: "auto" }}>
            {data.low_stock_products?.length === 0 && <div style={{ textAlign: "center", color: "var(--text-muted)", padding: 24 }}>All stock is healthy</div>}
            {data.low_stock_products?.map((p) => (
              <div key={p.id} data-testid={`low-stock-${p.style_id}`} style={{ display: "flex", gap: 12, padding: "12px 16px", borderBottom: "1px solid var(--border)", alignItems: "center" }}>
                {p.image_path ? <img className="thumb" src={resolveImage(p.image_path)} alt=""/> : <div className="thumb"/>}
                <div style={{ flex: 1 }}>
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <span className="badge badge-warn badge-lg" data-testid={`low-style-${p.style_id}`}>{p.style_id}</span>
                    <span style={{ fontWeight: 500 }}>{p.name}</span>
                  </div>
                  <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 4, display: "flex", gap: 6, flexWrap: "wrap" }}>
                    {p.low_variants.slice(0, 5).map((v, i) => (
                      <span key={i} style={{ background: "#FFF7ED", color: "#C2410C", padding: "1px 8px", borderRadius: 3, fontSize: 11 }}>{v.color} · {v.size}: {v.qty}</span>
                    ))}
                    {p.low_variants.length > 5 && <span>+{p.low_variants.length - 5} more</span>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function Kpi({ icon: Icon, label, value, foot, tid, tone }) {
  const accent = tone === "warn" ? "#F97316" : "#94A3B8";
  return (
    <div className="kpi" data-testid={tid}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div className="kpi-label">{label}</div>
        <Icon size={18} color={accent}/>
      </div>
      <div className="kpi-value">{value}</div>
      {foot && <div className="kpi-foot">{foot}</div>}
    </div>
  );
}

function BreakBox({ color, border, icon: Icon, label, value, tid, sub }) {
  return (
    <div data-testid={tid} style={{ background: color, border: `1px solid ${border}`, borderRadius: 4, padding: 16 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div className="kpi-label">{label}</div>
        <Icon size={18} color="#475569"/>
      </div>
      <div className="kpi-value" style={{ fontSize: 24 }}>{value}</div>
      {sub}
    </div>
  );
}
